import React, { useMemo, useState, useEffect } from "react";
import { TrashIcon, PlusIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

import { Toggle } from "../form-fields/Toggle";
import { SELECTED_TYPE } from "../panels/GoalWorkflowPanel";
import { NODE_TYPE } from "../../services/Constants";
import { ACTION_TYPE_META } from "../workflow/WorkflowCards";
import { APIs } from "../../services/Apis";

// Status configuration
export const STATUS_CONFIG = {
  active: {
    color: "text-green-600",
    bgColor: "bg-green-50",
    dotStyle: { backgroundColor: "#22c55e" },
    label: "Active",
  },
  pending: {
    color: "text-yellow-600",
    bgColor: "bg-yellow-50",
    dotStyle: { backgroundColor: "#facc15" },
    label: "Pending Approval",
  },
  inactive: {
    color: "text-orange-600",
    bgColor: "bg-orange-50",
    dotStyle: { backgroundColor: "#f97316" },
    label: "Inactive",
  },
  not_enabled: {
    color: "text-gray-600",
    bgColor: "bg-gray-50",
    dotStyle: { backgroundColor: "#9ca3af" },
    label: "Not Enabled",
  },
};

export function getStatusConfig(approved, ready, active) {
  if (!approved && ready) {
    return STATUS_CONFIG.pending;
  }
  if (approved && active) {
    return STATUS_CONFIG.active;
  }
  if (approved && !active) {
    return STATUS_CONFIG.inactive;
  }
  return STATUS_CONFIG.not_enabled;
}

export function isDelayAction(action) {
  return action?.type === NODE_TYPE.DELAY || action?.type === "delay";
}

export function countRealActions(actions = []) {
  return actions.filter((action) => !isDelayAction(action)).length;
}

export function getSegmentActions(segment) {
  if (!segment) return [];
  if (segment.actions?.length > 0) return segment.actions;
  if (segment.segmentActions?.length > 0) return segment.segmentActions;
  return [];
}

function delayAmountLabel(delay, delayUnit = "days") {
  const amount = Number(delay) || 0;
  if (amount === 0) return "Immediately";
  const unitMap = {
    minutes: amount === 1 ? "minute" : "minutes",
    hours: amount === 1 ? "hour" : "hours",
    days: amount === 1 ? "day" : "days",
  };
  const unitLabel =
    unitMap[delayUnit] ||
    (amount === 1 ? String(delayUnit).slice(0, -1) : delayUnit);
  return `${amount} ${unitLabel}`;
}

function getActionLabel(action) {
  const meta = ACTION_TYPE_META[action?.type];
  if (meta) return meta.label;
  if (action?.emailContent !== undefined) return "Email";
  return action?.type || "Action";
}

function isEmailAction(action) {
  return (
    action?.type === NODE_TYPE.EMAIL ||
    action?.type === "email" ||
    action?.emailContent !== undefined
  );
}

// Renders the email body as plain text using an inert document, so nothing in
// the generated HTML can load or execute.
function htmlToText(html) {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(String(html), "text/html");
    return (doc.body?.textContent || "").replace(/\s+/g, " ").trim();
  } catch {
    return "";
  }
}

const TAG_STYLES = {
  goal: "text-amber-700 bg-amber-50 border-amber-200",
  email: "text-blue-700 bg-blue-50 border-blue-200",
  sms: "text-emerald-700 bg-emerald-50 border-emerald-200",
  neutral: "text-gray-500 bg-gray-50 border-gray-200",
};

function Tag({ tone = "neutral", withDot = true, children }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap ${TAG_STYLES[tone] || TAG_STYLES.neutral}`}
    >
      {withDot && (
        <span className="h-1.5 w-1.5 rounded-full bg-current opacity-85" />
      )}
      {children}
    </span>
  );
}

function PendingApprovalBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 whitespace-nowrap">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="h-3.5 w-3.5"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
      </svg>
      Pending Approval
    </span>
  );
}

function CardShimmer({ label }) {
  return (
    <div className="flex animate-pulse flex-col gap-2">
      <div className="h-2.5 w-full rounded bg-slate-200" />
      <div className="h-2.5 w-5/6 rounded bg-slate-200" />
      <div className="h-2.5 w-4/6 rounded bg-slate-200" />
      <span className="mt-1 text-xs font-medium text-slate-400">{label}</span>
    </div>
  );
}

function Connector() {
  return (
    <div className="relative h-px w-[22px] shrink-0 self-center bg-gray-300">
      <span className="absolute -top-[3px] right-0 h-[5px] w-[5px] rotate-45 border-t border-r border-gray-300" />
    </div>
  );
}

const CARD_BASE =
  "relative flex h-[204px] w-[236px] shrink-0 cursor-pointer flex-col overflow-hidden rounded-xl border bg-white transition-colors";

function StripCard({
  label,
  tag,
  selected = false,
  onClick,
  children,
  footer,
}) {
  return (
    <div
      onClick={onClick}
      className={`${CARD_BASE} ${
        selected
          ? "border-primary ring-3 ring-primary/15"
          : "border-gray-200 hover:border-gray-300"
      }`}
    >
      <div className="flex flex-none items-center justify-between gap-2 border-b border-gray-100 px-3 py-2.5">
        <span className="min-w-0 truncate text-[13px] font-semibold text-gray-900">
          {label}
        </span>
        {tag}
      </div>
      <div className="flex min-h-0 flex-1 flex-col px-3 py-2.5">{children}</div>
      {footer && (
        <div className="flex flex-none items-center gap-0.5 border-t border-gray-100 px-2 py-1.5">
          {footer}
        </div>
      )}
    </div>
  );
}

function CardButton({ onClick, children, className = "" }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className={`cursor-pointer rounded-md px-2.5 py-1.5 text-[11.5px] font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 ${className}`}
    >
      {children}
    </button>
  );
}

function DeleteCardButton({ onClick, title }) {
  return (
    <button
      type="button"
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className="ml-auto flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
    >
      <TrashIcon className="h-[15px] w-[15px]" />
    </button>
  );
}

export function GoalSection({
  segment,
  isSelectedSegment = false,
  selectedType,
  selectedActionId,
  isOnboarding = false,
  onActionOverride,
  onSelectGoal,
  onSelectAction,
  onAnalytics,
  onAddAction,
  onDeleteGoal,
  onDeleteAction,
  onRefresh,
}) {
  const actions = useMemo(() => getSegmentActions(segment), [segment]);

  const [enabling, setEnabling] = useState(false);
  const [localActive, setLocalActive] = useState(segment?.active ?? false);

  useEffect(() => {
    setLocalActive(segment?.active ?? false);
  }, [segment?.active]);

  const allApproved = useMemo(() => {
    if (!segment) return false;
    if (!segment.approved) return false;
    if (actions.length === 0) return false;
    return actions.every((action) => action.approved);
  }, [segment, actions]);

  const statusConfig = getStatusConfig(
    segment?.approved,
    segment?.ready,
    segment?.active,
  );
  const isPendingApproval = !segment?.approved && segment?.ready;
  const goalNotReady = segment?.ready === false;

  const handleToggleEnable = async (newActive) => {
    if (!segment) return;
    if (isOnboarding) {
      if (onActionOverride) onActionOverride();
      return;
    }
    const targetId = segment.id || segment.segmentId;

    if (newActive && !allApproved) {
      toast.error(
        "All actions and the trigger must be approved before enabling this workflow.",
      );
      return;
    }

    setEnabling(true);
    try {
      const response = await APIs.enableSegment(targetId, newActive);
      if (response?.success) {
        setLocalActive(newActive);
        toast.success(
          newActive
            ? "Workflow enabled successfully!"
            : "Workflow disabled successfully!",
        );
        if (onRefresh) onRefresh();
      } else {
        toast.error(response?.message || "Failed to update workflow status");
      }
    } catch (error) {
      console.error("Error toggling workflow:", error);
    } finally {
      setEnabling(false);
    }
  };

  // Build the strip: goal card, then one card per action (delays included),
  // separated by connectors.
  const actionCards = useMemo(() => {
    const cards = [];
    let previousWasDelay = false;
    let isFirstAction = true;
    let emailCount = 0;

    for (const action of actions) {
      const actionId = action.id || action.segmentActionId;
      const isSelected =
        isSelectedSegment &&
        selectedType === SELECTED_TYPE.ACTION &&
        selectedActionId != null &&
        String(selectedActionId) === String(actionId);

      if (isDelayAction(action)) {
        cards.push({
          key: actionId || `delay-${cards.length}`,
          node: (
            <StripCard
              label="Wait"
              tag={<Tag tone="neutral">Delay</Tag>}
              selected={isSelected}
              onClick={() => onSelectAction(segment, action)}
              footer={
                <>
                  <CardButton onClick={() => onSelectAction(segment, action)}>
                    Edit
                  </CardButton>
                  <DeleteCardButton
                    title="Delete action"
                    onClick={() => onDeleteAction(action)}
                  />
                </>
              }
            >
              <div className="m-auto text-center text-[26px] font-semibold tracking-tight text-gray-900">
                {delayAmountLabel(action.delay, action.delayUnit)}
              </div>
            </StripCard>
          ),
        });
        previousWasDelay = true;
        isFirstAction = false;
        continue;
      }

      const isEmail = isEmailAction(action);
      const typeLabel = getActionLabel(action);
      let label = typeLabel;
      if (isEmail) {
        emailCount++;
        label = emailCount > 1 ? `Follow Up Email ${emailCount - 1}` : "Email";
      }

      // The first action, with no delay ahead of it, fires straight away.
      const showsImmediately = isFirstAction && !previousWasDelay;
      const tone = isEmail
        ? "email"
        : action.type === NODE_TYPE.SMS || action.type === "sms"
          ? "sms"
          : "neutral";

      const notReady = action.ready === false;
      const bodyText =
        htmlToText(action.htmlEmailContent || action.emailContent) ||
        "Sent to everyone matching the goal.";

      cards.push({
        key: actionId || `action-${cards.length}`,
        node: (
          <StripCard
            label={label}
            tag={
              showsImmediately ? (
                <Tag tone="neutral" withDot={false}>
                  Immediately
                </Tag>
              ) : (
                <Tag tone={tone}>{typeLabel}</Tag>
              )
            }
            selected={isSelected}
            onClick={() => onSelectAction(segment, action)}
            footer={
              <>
                <CardButton onClick={() => onSelectAction(segment, action)}>
                  Edit
                </CardButton>
                {action.approved === false && (
                  <span className="px-1 text-[11px] font-medium text-amber-600">
                    Pending
                  </span>
                )}
                <DeleteCardButton
                  title="Delete action"
                  onClick={() => onDeleteAction(action)}
                />
              </>
            }
          >
            {notReady ? (
              <CardShimmer
                label={isEmail ? "Generating email..." : "Generating action..."}
              />
            ) : (
              <>
                <div className="mb-2 line-clamp-2 shrink-0 text-[12.5px] font-semibold leading-snug text-gray-900">
                  {action.emailSubject || action.title || typeLabel}
                </div>
                <p className="m-0 line-clamp-3 max-h-[58px] shrink-0 overflow-hidden text-xs leading-relaxed text-slate-600">
                  {bodyText}
                </p>
              </>
            )}
          </StripCard>
        ),
      });

      previousWasDelay = false;
      isFirstAction = false;
    }

    return cards;
  }, [
    actions,
    segment,
    isSelectedSegment,
    selectedType,
    selectedActionId,
    onSelectAction,
    onDeleteAction,
  ]);

  if (!segment) return null;

  return (
    <section className="flex flex-col">
      {/* Goal header */}
      <div className="mb-3 flex flex-wrap items-center gap-2.5 px-4 sm:px-6 lg:px-8">
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={statusConfig.dotStyle}
          title={statusConfig.label}
        />
        <span className="text-sm font-semibold text-gray-900">
          {segment.description || segment.title || "Untitled Goal"}
        </span>
        <span className="text-xs text-gray-500">
          · {statusConfig.label} ·{" "}
          {countRealActions(actions) === 1
            ? "1 action"
            : `${countRealActions(actions)} actions`}
        </span>
        <span className="ml-auto flex items-center gap-2 text-xs text-gray-500">
          {isPendingApproval && <PendingApprovalBadge />}
          {!isOnboarding && !isPendingApproval && (
            <>
              <span>{localActive ? "Active" : "Inactive"}</span>
              <Toggle
                checked={localActive}
                onChange={handleToggleEnable}
                disabled={enabling}
                label="Activate workflow"
              />
            </>
          )}
          <button
            type="button"
            onClick={() => onDeleteGoal(segment)}
            className="cursor-pointer rounded-md px-2 py-1 text-xs font-medium text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
          >
            Delete
          </button>
        </span>
      </div>

      {/* Card strip */}
      <div className="flex items-stretch gap-0 overflow-x-auto px-4 pt-1 pb-3 sm:px-6 lg:px-8">
        <StripCard
          label="Goal"
          tag={<Tag tone="goal">Goal</Tag>}
          selected={isSelectedSegment && selectedType === SELECTED_TYPE.GOAL}
          onClick={() => onSelectGoal(segment)}
          footer={
            !goalNotReady && (
              <>
                <CardButton onClick={() => onSelectGoal(segment)}>
                  Edit Goal
                </CardButton>
                <CardButton onClick={() => onAnalytics(segment)}>
                  Analytics
                </CardButton>
              </>
            )
          }
        >
          {goalNotReady ? (
            <CardShimmer label="Generating goal..." />
          ) : (
            <>
              <div className="mb-2 line-clamp-2 shrink-0 text-[12.5px] font-semibold leading-snug text-gray-900">
                {segment.outcomeGoal ||
                  segment.title ||
                  segment.description ||
                  "Untitled Goal"}
              </div>
              <p className="m-0 line-clamp-3 max-h-[58px] shrink-0 overflow-hidden text-xs leading-relaxed text-slate-600">
                {segment.humanExplanationQuery || segment.description || ""}
              </p>
            </>
          )}
        </StripCard>

        {actionCards.map((card, index) => (
          <React.Fragment key={card.key || index}>
            <Connector />
            {card.node}
          </React.Fragment>
        ))}

        <Connector />

        <button
          type="button"
          onClick={() => onAddAction(segment)}
          className="flex h-[204px] w-[236px] shrink-0 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50/60 text-[12.5px] font-medium text-gray-400 transition-colors hover:border-primary hover:text-primary"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full border border-dashed border-gray-300">
            <PlusIcon className="h-4 w-4" />
          </span>
          Add action
        </button>
      </div>
    </section>
  );
}
